import { AfterViewInit, Component, ElementRef, LOCALE_ID, OnDestroy, OnInit, inject } from '@angular/core';
import { DOCUMENT, DecimalPipe } from '@angular/common';
import { RouterModule } from '@angular/router';
import { DomSanitizer, Meta, SafeResourceUrl, Title } from '@angular/platform-browser';
import { links } from '../../shared/links';
import { initFeatureFx } from '../feature-fx.util';

/**
 * Kommerzielle SEO-Landingpage „Automatenkiosk“ (/automatenkiosk).
 *
 * Ziel-Keywords: „automatenkiosk“ (~3.250/Mon., CPC 2,70 €) + „24/7 kiosk“
 * (~3.000) + „kiosk eröffnen“ (~350, do=100). Intent ist KONZEPT-lastig
 * (know ~52 %), daher bewusst als HYBRID gebaut: erst „Was ist ein
 * Automatenkiosk?“ + „In 3 Schritten eröffnen“ (holt die Recherche-Mehrheit ab),
 * dann kommerzieller Funnel (Vorteile, Live-Beispiel, Preise, Shop-CTA).
 *
 * Abgrenzung zu /automatenladen (eigener, kaum überlappender SERP): dort steht
 * das Kombinieren mehrerer Automaten zu einem Laden im Fokus; hier das
 * 24/7-Kiosk-Konzept + der Weg zum eigenen Kiosk. Beide verlinken sich gegenseitig.
 * Referenz-Live-Box: Automatenkiosk Langenargen. Blog: mit-einem-automaten-kiosk-voll-im-trend.
 */
@Component({
  selector: 'app-automatenkiosk-page',
  standalone: true,
  imports: [RouterModule, DecimalPipe],
  templateUrl: './automatenkiosk-page.component.html',
  styleUrls: ['../cluster-landing.shared.scss'],
})
export class AutomatenkioskPageComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly title = inject(Title);
  private readonly meta = inject(Meta);
  private readonly document = inject(DOCUMENT);
  private readonly locale = inject(LOCALE_ID);
  private readonly host: ElementRef<HTMLElement> = inject(ElementRef);
  private readonly sanitizer = inject(DomSanitizer);
  private observers: IntersectionObserver[] = [];
  private revealFallback?: ReturnType<typeof setTimeout>;

  readonly links = links;
  readonly blogSlug = 'mit-einem-automaten-kiosk-voll-im-trend';

  readonly liveUrl: SafeResourceUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
    'https://live.marktbox.de/automatenkiosk-langenargen/U-MB-00082'
  );

  readonly priceBuyFrom = 6800;
  readonly priceRentFrom = 300;

  readonly faqs: { q: string; a: string }[] = [
    {
      q: $localize`:@@kiosk.faq1.q:Was ist ein Automatenkiosk?`,
      a: $localize`:@@kiosk.faq1.a:Ein Automatenkiosk ist ein kleiner Laden, der rund um die Uhr aus Automaten verkauft – ganz ohne Personal. Statt Öffnungszeiten und Kasse zieht die Kundschaft ihre Ware direkt am Automaten und zahlt bargeldlos. Mehrere Automaten zusammen bilden ein komplettes 24/7-Sortiment.`,
    },
    {
      q: $localize`:@@kiosk.faq2.q:Wie eröffne ich einen Automatenkiosk?`,
      a: $localize`:@@kiosk.faq2.a:In drei Schritten: einen gut sichtbaren Standort mit Laufkundschaft wählen, die passenden Automaten aussuchen (die marktbox ist am flexibelsten für unterschiedliche Waren) und bestücken. Danach läuft der Verkauf automatisch – Füllstände und Zahlungen steuerst du per App.`,
    },
    {
      q: $localize`:@@kiosk.faq3.q:Was kostet ein Automatenkiosk?`,
      a: $localize`:@@kiosk.faq3.a:Der Einstieg ist mit einer marktbox ab 6.800 € (Kauf) oder ab 300 € pro Monat (Miete) möglich. Ein Kiosk lässt sich klein starten und Automat für Automat erweitern – so wächst die Investition mit dem Sortiment.`,
    },
    {
      q: $localize`:@@kiosk.faq4.q:Lohnt sich ein 24/7-Automatenkiosk?`,
      a: $localize`:@@kiosk.faq4.a:Ein Kiosk ohne Personal verkauft rund um die Uhr – auch abends, sonntags und feiertags – und spart laufende Personalkosten. Ob es sich rechnet, hängt von Standort, Sortiment und Frequenz ab. Praxiswissen dazu findest du in unserem Ratgeber.`,
    },
  ];

  ngOnInit(): void {
    const canonical = this.canonicalUrl();
    const title = $localize`:@@kiosk.metaTitle:Automatenkiosk eröffnen – 24/7 Kiosk ohne Personal | marktbox`;
    const description =
      $localize`:@@kiosk.metaDesc:Automatenkiosk eröffnen: 24/7-Verkauf ohne Personal. In 3 Schritten zum eigenen Kiosk mit der marktbox. Ab 6.800 € oder 300 €/Monat.`;

    this.title.setTitle(title);
    this.meta.updateTag({ name: 'description', content: description });
    this.meta.updateTag({
      name: 'keywords',
      content: $localize`:@@kiosk.metaKeywords:Automatenkiosk, 24/7 Kiosk, Automatenkiosk eröffnen, Kiosk eröffnen, 24 Stunden Kiosk, Kiosk ohne Personal, Automatenladen, marktbox`,
    });
    this.meta.updateTag({ property: 'og:type', content: 'website' });
    this.meta.updateTag({ property: 'og:site_name', content: 'marktbox' });
    this.meta.updateTag({ property: 'og:title', content: title });
    this.meta.updateTag({ property: 'og:description', content: description });
    this.meta.updateTag({ property: 'og:url', content: canonical });
    this.meta.updateTag({
      property: 'og:image',
      content: 'https://www.marktbox.de/assets/headers/marktbox-00-1920.webp',
    });

    this.setCanonical(canonical);
    this.injectJsonLd('automatenkiosk-product-schema', {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: 'marktbox – Automat für den Automatenkiosk',
      description,
      image: 'https://www.marktbox.de/assets/headers/marktbox-00-1920.webp',
      brand: { '@type': 'Brand', name: 'marktbox' },
      category: 'Verkaufsautomat',
      offers: [
        {
          '@type': 'Offer',
          name: 'Kauf',
          priceCurrency: 'EUR',
          price: this.priceBuyFrom,
          availability: 'https://schema.org/InStock',
          itemCondition: 'https://schema.org/NewCondition',
          url: canonical,
          seller: { '@type': 'Organization', name: 'marktbox' },
        },
        {
          '@type': 'Offer',
          name: 'Miete',
          priceCurrency: 'EUR',
          price: this.priceRentFrom,
          availability: 'https://schema.org/InStock',
          url: canonical,
          seller: { '@type': 'Organization', name: 'marktbox' },
        },
      ],
    });
    this.injectJsonLd('automatenkiosk-faq-schema', {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: this.faqs.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    });
  }

  ngAfterViewInit(): void {
    this.observers = initFeatureFx(this.host.nativeElement);
    if (typeof window !== 'undefined' && typeof IntersectionObserver !== 'undefined') {
      this.revealFallback = setTimeout(() => {
        this.host.nativeElement
          .querySelectorAll('.reveal:not(.reveal-in)')
          .forEach((el) => el.classList.add('reveal-in'));
      }, 1200);
    }
  }

  ngOnDestroy(): void {
    this.observers.forEach((o) => o.disconnect());
    this.observers = [];
    if (this.revealFallback) clearTimeout(this.revealFallback);
  }

  scrollTo(event: Event, id: string): void {
    event.preventDefault();
    this.document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  private canonicalUrl(): string {
    const localePrefix = this.locale.startsWith('da') ? 'da' : this.locale.startsWith('nl') ? 'nl' : 'de';
    return `https://marktbox.de/${localePrefix}/automatenkiosk`;
  }

  private setCanonical(href: string): void {
    let link = this.document.querySelector<HTMLLinkElement>('link[rel=canonical]');
    if (!link) {
      link = this.document.createElement('link');
      link.rel = 'canonical';
      this.document.head.appendChild(link);
    }
    link.href = href;
  }

  private injectJsonLd(id: string, obj: object): void {
    this.document.getElementById(id)?.remove();
    const script = this.document.createElement('script');
    script.id = id;
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(obj);
    this.document.head.appendChild(script);
  }
}
